import React from 'react';
import Label from '../Label/Label';
import 'react-datepicker/dist/react-datepicker.css';
import { default as ReactSelect } from 'react-select';
import { FieldError } from 'react-hook-form';
import useTranslation from '../../../../Hooks/useTranslation';
import { firstUpperString } from '../FirstUpper';
import styles from './Select.module.scss';
import FieldErrorMessage from '../../../Base/Components/FieldErrorMessage/FieldErrorMessage';
import useSelectValueGuard from '../../Hooks/useSelectValueGuard';

interface ISelect {
	onChange?: (value: SelectValue) => void;
	onChangeExtra?: (value: SelectValue) => void;
	onFocus?: any;
	placeholder?: string;
	required?: boolean;
	value?: SelectValue | string;
	label?: string;
	firstUpper?: boolean;
	className?: string;
	options: SelectValue[];
	isSearchable?: boolean;
	error?: FieldError;
	returnKey?: string;
	translate?: boolean;
	disabled?: boolean;
	isMulti?: boolean;
	components?: object;
	closeMenuOnSelect?: boolean;
	hideSelectedOptions?: boolean;
	isClearable?: boolean;
	portal?: boolean;
	firstUpperValues?: boolean;
	displayErrorMessage?: boolean;
}

export interface SelectValue {
	value: string | object;
	label: string;
}

export default function Select(props: ISelect) {
	const {
		label,
		onChange,
		onChangeExtra,
		onFocus,
		placeholder = 'base.shared.pick',
		required = false,
		value,
		firstUpper = false,
		firstUpperValues = false,
		className,
		options,
		error,
		isSearchable = false,
		returnKey,
		translate,
		disabled,
		isMulti = false,
		components = {},
		closeMenuOnSelect,
		hideSelectedOptions,
		isClearable = false,
		portal = false,
		displayErrorMessage = true,
	} = props;
	const { t } = useTranslation();

	const translateOptions = (options: SelectValue[]): SelectValue[] => {
		if (firstUpperValues) {
			return options.map((option: SelectValue) => ({
				...option,
				label: firstUpperString(t(option.label) ?? ''),
			}));
		}
		return options.map((option: SelectValue) => ({
			...option,
			label: t(option.label),
		}));
	};

	const handleOnChange = e => {
		let val = e;
		if (returnKey) {
			val = val[returnKey] || val;
		}

		onChange?.(val);
		onChangeExtra?.(val);
	};

	const translatedOptions = translateOptions(options);

	useSelectValueGuard({ onChange, options: translatedOptions, value });

	return (
		<div className={styles.wrapper}>
			{label && <Label tx={label} firstUpper={firstUpper} />}
			<ReactSelect
				isMulti={isMulti}
				value={value}
				className={error && (error?.value || error?.message) && 'invalid'}
				defaultValue={value}
				options={translatedOptions}
				classNamePrefix={'reactSelect'}
				styles={
					error
						? {
								control: () => ({
									borderColor: 'red !important',
								}),
						  }
						: undefined
				}
				isSearchable={isSearchable}
				placeholder={t(placeholder)}
				onChange={handleOnChange}
				menuPosition='absolute'
				isDisabled={disabled}
				components={components}
				closeMenuOnSelect={closeMenuOnSelect}
				hideSelectedOptions={hideSelectedOptions}
				isClearable={isClearable}
				menuPortalTarget={portal ? document.body : null}
				noOptionsMessage={() => t('base.noOptions')}
			/>
			{error?.message && displayErrorMessage && (
				<FieldErrorMessage tx={error?.message} />
			)}
		</div>
	);
}
